import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TableCell, TableHead } from "@/components/ui/table";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/i18n";

let studioCache = null;
const fieldsCache = {};
export function useStudioEnabled() {
  const [on, setOn] = useState(studioCache);
  useEffect(() => { if (studioCache === null) api.get("/studio/status").then((r) => { studioCache = r.data.enabled; setOn(r.data.enabled); }).catch(() => setOn(false)); }, []);
  return !!on;
}

export function useListFields(model) {
  const enabled = useStudioEnabled();
  const [fields, setFields] = useState(fieldsCache[model] || []);
  useEffect(() => { if (!enabled) return; api.get(`/studio/fields?model=${model}`).then((r) => { fieldsCache[model] = r.data.filter((f) => f.show_in_list); setFields(fieldsCache[model]); }).catch(() => {}); }, [enabled, model]);
  return enabled ? fields : [];
}

export const fmtCustom = (f, v) => v == null || v === "" ? "—" : f.type === "checkbox" ? (v ? "Yes" : "No") : f.type === "date" ? String(v).slice(0, 10) : f.type === "number" || f.type === "formula" ? Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : String(v);

export function CustomHeads({ fields }) {
  const { t } = useT();
  return fields.map((f) => <TableHead key={f.key} data-testid={`col-head-${f.key}`}>{t(`cf.${f.model}.${f.key}`, f.label)}</TableHead>);
}

export function CustomCells({ fields, doc }) {
  return fields.map((f) => <TableCell key={f.key} className="text-xs font-mono" data-testid={`col-${f.key}-${doc.id}`}>{fmtCustom(f, (doc.custom || {})[f.key])}</TableCell>);
}

export function CustomFieldInputs({ fields, values, onChange, model }) {
  const { t } = useT();
  return fields.map((f) => {
    const v = values[f.key] ?? "";
    const label = t(`cf.${model}.${f.key}`, f.label);
    if (f.type === "formula") return <Field key={f.key} label={`${label} (formula)`} hint={f.formula}><Input data-testid={`cf-${f.key}`} readOnly value={v ?? ""} className="bg-secondary/40 font-mono" /></Field>;
    if (f.type === "checkbox") return <Field key={f.key} label={label}><input data-testid={`cf-${f.key}`} type="checkbox" className="h-4 w-4" checked={!!v} onChange={(e) => onChange(f.key, e.target.checked)} /></Field>;
    if (f.type === "select") return <Field key={f.key} label={label}><select data-testid={`cf-${f.key}`} className="w-full border rounded px-2 py-2 text-sm bg-card" value={v} onChange={(e) => onChange(f.key, e.target.value)}><option value="">—</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select></Field>;
    return <Field key={f.key} label={label} hint={f.help}><Input data-testid={`cf-${f.key}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={f.type === "date" && v ? String(v).slice(0, 10) : v} onChange={(e) => onChange(f.key, e.target.value)} /></Field>;
  });
}

export function CustomFieldsButton({ model, docId, label }) {
  const enabled = useStudioEnabled();
  const [open, setOpen] = useState(false);
  if (!enabled) return null;
  return <>
    <Button size="sm" variant="ghost" data-testid={`custom-fields-${docId}`} title="Custom fields (Studio)" onClick={() => setOpen(true)}><SlidersHorizontal className="h-3.5 w-3.5" /></Button>
    {open && <CustomFieldsDialog model={model} docId={docId} label={label} onClose={() => setOpen(false)} />}
  </>;
}

function CustomFieldsDialog({ model, docId, label, onClose }) {
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({});
  useEffect(() => { api.get(`/studio/values/${model}/${docId}`).then((r) => { setData(r.data); setVals(r.data.values || {}); }); }, [model, docId]);
  const save = async () => { try { const { data: d } = await api.put(`/studio/values/${model}/${docId}`, { values: vals }); setVals(d.values); toast.success("Custom fields saved"); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  return <Dialog open onOpenChange={onClose}><DialogContent className="bg-card" data-testid="custom-fields-dialog"><DialogHeader><DialogTitle className="font-heading">Custom fields {label && <span className="font-mono text-sm text-muted-foreground">{label}</span>}</DialogTitle></DialogHeader>
    {!data ? <div className="text-sm text-muted-foreground">Loading…</div> : data.fields.length === 0 ? <div className="text-sm text-muted-foreground">No custom fields defined for this model. Add them in Studio.</div> : <div className="grid grid-cols-2 gap-3"><CustomFieldInputs model={model} fields={data.fields} values={vals} onChange={(k, v) => setVals({ ...vals, [k]: v })} /></div>}
    <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button>{data?.fields.length > 0 && <Button data-testid="custom-fields-save" className="bg-blue-600 hover:bg-blue-700" onClick={save}>Save</Button>}</DialogFooter></DialogContent></Dialog>;
}
